// Explorer CSV export (Phase 3a finish) — PURE (no DOM). Exports the CURRENTLY
// DISPLAYED grouped view: the active dimension columns + a Subtotal flag + the
// visible stat columns, in the on-screen order (respecting the current sort, since
// the caller passes the already-sorted display rows). Subtotal rows are included +
// flagged so the file mirrors the screen. Money is a plain number when the node is
// single-currency (spreadsheet-friendly), else the per-currency display string
// (NEVER a summed-across-currencies number); a Currency column carries the group's
// currencies. papaparse handles quoting.

import Papa from 'papaparse';
import type { ReportingDimension } from '../../types/reporting';
import { formatCurrencyAmounts } from '../../lib/reportingMoney';
import {
  attributeText,
  columnByKey,
  DIMENSION_LABEL,
  isStatKey,
  statCount,
  statKind,
  statMoney,
  statPct,
  type BreakdownColumnKey,
  type BreakdownStatKey,
} from './breakdownColumns';
import type { RollupDisplayRow } from './breakdownGrouping';

const round2 = (x: number): number => Math.round(x * 100) / 100;

const labelFor = (key: BreakdownColumnKey): string => columnByKey(key)?.label ?? key;

// One stat → a CSV cell: '' when null/absent, a number when countable / single-
// currency money, else the per-currency display string (never a summed number).
export function statCsvValue(row: RollupDisplayRow, key: BreakdownStatKey): number | string {
  const m = row.metrics;
  const kind = statKind(key);
  if (kind === 'count') return statCount(m, key);
  if (kind === 'pct') {
    const p = statPct(m, key);
    return p === null ? '' : round2(p);
  }
  const money = statMoney(m, key);
  if (money.length === 0) return '';
  if (money.length === 1) return round2(money[0]!.amount);
  return formatCurrencyAmounts(money);
}

// One column → its CSV cell (metric via statCsvValue, attribute via attributeText —
// e.g. the status word / type label / "12.50/day", blank on non-native rows).
function columnCsvValue(row: RollupDisplayRow, key: BreakdownColumnKey): number | string {
  if (isStatKey(key)) return statCsvValue(row, key);
  const column = columnByKey(key);
  return column ? attributeText(row, column) : '';
}

export function buildBreakdownCsv(
  rows: readonly RollupDisplayRow[],
  activeDims: readonly ReportingDimension[],
  columns: readonly BreakdownColumnKey[],
): string {
  const fields = [
    ...activeDims.map((d) => DIMENSION_LABEL[d]),
    'Subtotal',
    'Currency',
    ...columns.map(labelFor),
  ];

  const data = rows.map((row) => {
    // The node's name sits in its OWN dim column; shallower/deeper columns blank —
    // mirrors the on-screen staircase.
    const dimCells = activeDims.map((_, i) => (i === row.level ? row.name ?? row.id ?? '' : ''));
    return [
      ...dimCells,
      row.isSubtotal ? 'subtotal' : 'leaf',
      row.metrics.currencies.join(' '),
      ...columns.map((key) => columnCsvValue(row, key)),
    ];
  });

  return Papa.unparse({ fields, data });
}

// Filename like explorer_2026-06-01_2026-06-30.csv.
export function breakdownCsvFilename(from: string, to: string): string {
  return `explorer_${from}_${to}.csv`;
}
